import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '../components/ui/button';
import LanguageSelector from '../components/LanguageSelector';
import useForceLightTheme from '../hooks/useForceLightTheme';
import {
  Clock, MapPin, FileText, CheckCircle, ArrowRight, Zap,
  QrCode, Camera, Globe2, Users2, Wallet, ShieldCheck, Menu, X, Star, ChevronDown
} from 'lucide-react';

const PRICING = {
  usd: { symbol: '$', setup: '9.99', monthly: '5.99', label: 'USD', flag: '🇺🇸' },
  cad: { symbol: 'C$', setup: '9.99', monthly: '5.99', label: 'CAD', flag: '🇨🇦' },
  eur: { symbol: '€', setup: '9.99', monthly: '5.99', label: 'EUR', flag: '🇪🇺' },
  gbp: { symbol: '£', setup: '9.99', monthly: '5.99', label: 'GBP', flag: '🇬🇧' }
};

const getInitialCurrency = () => {
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const canada = ['America/Toronto', 'America/Vancouver', 'America/Edmonton', 'America/Winnipeg', 'America/Halifax', 'America/St_Johns', 'America/Regina', 'America/Montreal'];
    if (canada.includes(tz)) return 'cad';
    if (tz === 'Europe/London') return 'gbp';
    if (tz.startsWith('Europe/')) return 'eur';
    return 'usd';
  } catch {
    return 'usd';
  }
};

const HERO_PHOTO = 'https://images.unsplash.com/photo-1541888946425-d81bb19240f5?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200';
const BENEFITS_PHOTO = 'https://images.unsplash.com/photo-1626885930974-4b69aa21bbf9?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200';

const Landing = () => {
  useForceLightTheme();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [currency, setCurrency] = useState(getInitialCurrency);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [openFaq, setOpenFaq] = useState(0);
  const p = PRICING[currency];

  const faqs = [
    {
      q: 'How does billing work?',
      a: `You pay ${p.symbol}${p.monthly} per active user, per month, plus a one-time ${p.symbol}${p.setup} setup fee. Billing is handled securely through Stripe and starts automatically after your 30-day free trial ends. You can update your card or view invoices anytime from the in-app billing portal.`,
    },
    {
      q: 'Is the 30-day trial really free?',
      a: 'Yes — you get full access to every feature for 30 days at no charge. A credit card is required to start, but nothing is billed until the trial ends, and you can cancel before then with zero cost.',
    },
    {
      q: 'What happens when I add or remove staff?',
      a: 'Your bill adjusts automatically. Add a worker mid-month and they become a billed seat; deactivate one and you stop paying for them. You only ever pay for active users — no fixed tiers, no wasted seats.',
    },
    {
      q: 'How long does setup take?',
      a: 'Most companies are up and running the same day. Create your company account, invite your staff by email, and workers can clock in from their phones within minutes — no hardware, no installation, no training sessions required.',
    },
    {
      q: 'Am I locked into a contract?',
      a: 'No. Hines Time is month-to-month with no long-term contracts. Cancel anytime from your billing portal and you won\u2019t be charged again — your data stays safely archived if you ever come back.',
    },
    {
      q: 'What if I need help getting started?',
      a: 'In-app support chat, a full user manual, and email support are included with every account at no extra cost. We\u2019ll help you import your customers, set up jobs, and get your first payroll report out.',
    },
  ];

  const features = [
    {
      icon: MapPin,
      badge: 'GPS Verified',
      title: t('features.location'),
      description: t('features.location.desc'),
      accent: 'text-emerald-700 bg-emerald-50 border-emerald-200'
    },
    {
      icon: QrCode,
      badge: 'Hardware Free',
      title: 'QR Code & Kiosk Clock-In',
      description: 'Set up a tablet kiosk or printed QR codes at site entrances so crews clock in fast — with PIN verification.',
      accent: 'text-amber-700 bg-amber-50 border-amber-200'
    },
    {
      icon: Camera,
      badge: 'Audit Ready',
      title: 'Jobs with Photo Proof',
      description: 'Assign jobs, track progress, and require before/after photos with captions — proof your clients can see.',
      accent: 'text-blue-700 bg-blue-50 border-blue-200'
    },
    {
      icon: Wallet,
      badge: 'Payroll Ready',
      title: 'Payroll & Advanced Reports',
      description: 'Automatic overtime, pay period locks, activity analytics, and clean CSV exports for accounting.',
      accent: 'text-emerald-700 bg-emerald-50 border-emerald-200'
    },
    {
      icon: Users2,
      badge: 'Client Portal',
      title: 'Customer Portal',
      description: 'Give clients real-time visibility into their jobs, completion photos, and electronic sign-offs.',
      accent: 'text-amber-700 bg-amber-50 border-amber-200'
    },
    {
      icon: Globe2,
      badge: '5 Languages',
      title: 'Multi-Language Crew App',
      description: 'Keep multilingual crews confident with one-click switching between English, French, Spanish, German, and Italian.',
      accent: 'text-blue-700 bg-blue-50 border-blue-200'
    }
  ];

  const benefits = [
    'Reduce payroll errors by up to 80%',
    'Save 5+ hours per week on timesheet processing',
    'Real-time visibility of your entire workforce',
    'Mobile-friendly for field workers',
    'Automatic overtime calculations',
    'Photo documentation for completed jobs'
  ];

  const planFeatures = [
    'Unlimited time tracking', 'GPS location verification', 'Job management',
    'Photo documentation', 'Team organization', 'Mobile access',
    'Advanced reports', 'CSV exports', 'Time editing tools',
    'Shift management', 'Multi-administrator', 'Email & SMS notifications'
  ];

  return (
    <div className="landing-page min-h-screen bg-[#FAF8F5] text-zinc-900">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-[#FAF8F5]/85 backdrop-blur-xl border-b border-zinc-900/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16 sm:h-20">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-zinc-900 rounded-xl flex items-center justify-center">
                <Clock className="w-5 h-5 text-amber-400" />
              </div>
              <span className="text-xl sm:text-2xl font-extrabold tracking-tight text-zinc-900">Hines Time</span>
            </div>
            <div className="hidden md:flex items-center gap-3 lg:gap-6">
              <a href="#features" className="text-sm font-medium text-zinc-600 hover:text-zinc-900 transition-colors" data-testid="nav-features-link">Features</a>
              <a href="#pricing" className="text-sm font-medium text-zinc-600 hover:text-zinc-900 transition-colors" data-testid="nav-pricing-link">Pricing</a>
              <div data-testid="nav-language-selector" className="flex-shrink-0 min-w-[128px]"><LanguageSelector /></div>
              <Button
                onClick={() => navigate('/signin')}
                variant="outline"
                className="border-zinc-300 bg-white text-zinc-800 hover:bg-zinc-50 rounded-xl font-semibold"
                data-testid="nav-login-button"
              >
                Sign In
              </Button>
              <Button
                onClick={() => navigate('/register')}
                className="bg-amber-600 hover:bg-amber-700 text-white rounded-xl font-semibold shadow-lg shadow-amber-600/25"
                data-testid="nav-start-trial-button"
              >
                Get Started Free
              </Button>
            </div>
            <button
              className="md:hidden p-2 min-w-[44px] min-h-[44px] flex items-center justify-center text-zinc-700"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-testid="nav-mobile-menu-button"
              aria-label="Menu"
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
          {mobileMenuOpen && (
            <div className="md:hidden pb-4 flex flex-col gap-3" data-testid="nav-mobile-menu">
              <a href="#features" className="text-sm font-medium text-zinc-700 py-1" onClick={() => setMobileMenuOpen(false)}>Features</a>
              <a href="#pricing" className="text-sm font-medium text-zinc-700 py-1" onClick={() => setMobileMenuOpen(false)}>Pricing</a>
              <LanguageSelector />
              <Button onClick={() => navigate('/signin')} variant="outline" className="border-zinc-300 bg-white text-zinc-800 rounded-xl" data-testid="nav-mobile-login-button">Sign In</Button>
              <Button onClick={() => navigate('/register')} className="bg-amber-600 hover:bg-amber-700 text-white rounded-xl" data-testid="nav-mobile-start-trial-button">Get Started Free</Button>
            </div>
          )}
        </div>
      </nav>

      {/* Hero */}
      <header className="relative pt-28 sm:pt-36 pb-16 sm:pb-24 overflow-hidden">
        <div className="absolute inset-0 lp-grain pointer-events-none" aria-hidden="true"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8 items-center">
            <div className="lg:col-span-6 lp-fade-up">
              <div className="flex flex-wrap gap-2 mb-6">
                <span className="inline-flex items-center gap-1.5 bg-emerald-50 border border-emerald-300 text-[#065F46] px-3.5 py-1.5 rounded-full text-xs sm:text-sm font-bold" data-testid="landing-europe-banner">
                  <Globe2 className="w-3.5 h-3.5" />
                  Now in the UK &amp; Europe — billed in £ / €
                </span>
              </div>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.08] text-zinc-900 mb-6">
                {t('landing.hero.title')}
                <span className="block text-amber-600">{t('landing.hero.subtitle')}</span>
              </h1>
              <p className="text-lg text-zinc-600 leading-relaxed mb-8 max-w-xl">
                {t('landing.hero.description')}
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Button
                  onClick={() => navigate('/register')}
                  size="lg"
                  className="bg-amber-600 hover:bg-amber-700 text-white text-lg h-14 px-8 rounded-xl font-semibold shadow-lg shadow-amber-600/25 transition-transform hover:-translate-y-0.5"
                  data-testid="hero-start-trial-button"
                >
                  {t('landing.hero.startTrial')}
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
                <Button
                  onClick={() => navigate('/contact')}
                  size="lg"
                  variant="outline"
                  className="bg-[#F3F4F6] border-2 border-[#374151] text-zinc-900 hover:bg-zinc-200 text-lg h-14 px-8 rounded-xl font-semibold"
                  data-testid="hero-demo-button"
                >
                  {t('landing.hero.requestDemo')}
                </Button>
              </div>
              <div className="mt-8 flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-zinc-600">
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-emerald-600" />
                  <span>{t('landing.hero.noCard')}</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-emerald-600" />
                  <span>{t('landing.hero.quickSetup')}</span>
                </div>
              </div>
            </div>

            <div className="lg:col-span-6 relative lp-fade-up lp-delay-2" data-testid="hero-visual">
              <div className="relative">
                <img
                  src={HERO_PHOTO}
                  alt="Field crew at a job site"
                  className="w-full h-[340px] sm:h-[440px] object-cover rounded-3xl shadow-2xl"
                />
                {/* Floating clock-in card */}
                <div className="absolute -bottom-6 -left-2 sm:-left-6 bg-white rounded-2xl shadow-xl border border-zinc-100 p-4 sm:p-5 w-[240px] sm:w-[270px] lp-float" data-testid="hero-clockin-card">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-9 h-9 rounded-full bg-emerald-100 flex items-center justify-center">
                      <MapPin className="w-4 h-4 text-emerald-700" />
                    </div>
                    <div>
                      <p className="text-sm font-bold text-zinc-900">Marcus clocked in</p>
                      <p className="text-xs text-zinc-500">Riverside Site · 7:02 AM</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 text-xs font-semibold text-emerald-700 bg-emerald-50 rounded-lg px-2.5 py-1.5">
                    <span className="w-2 h-2 rounded-full bg-emerald-500 lp-pulse"></span>
                    GPS verified on site
                  </div>
                </div>
                {/* Floating stats card */}
                <div className="absolute -top-5 -right-2 sm:-right-4 bg-zinc-900 text-white rounded-2xl shadow-xl p-4 w-[180px] lp-float lp-delay-3" data-testid="hero-stats-card">
                  <p className="text-[11px] uppercase tracking-wider text-zinc-400 font-semibold mb-1">This Week</p>
                  <p className="text-2xl font-extrabold">847.5 hrs</p>
                  <p className="text-xs text-emerald-400 font-semibold mt-1">24 workers active now</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Trust strip */}
      <section className="border-y border-zinc-200 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {[
              { value: '30-Day', label: 'Free Trial' },
              { value: '50+', label: 'Features Included' },
              { value: '24/7', label: 'GPS Tracking' },
              { value: '100%', label: 'Secure & Compliant' }
            ].map((s) => (
              <div key={s.label} className="text-center md:text-left">
                <div className="text-3xl sm:text-4xl font-extrabold text-zinc-900">{s.value}</div>
                <div className="text-sm text-[#4B5563] font-semibold mt-1">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features */}
      <section id="features" className="py-20 sm:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl mb-14">
            <p className="text-xs font-bold uppercase tracking-wider text-[#B45309] mb-3">Built for the field</p>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-zinc-900 mb-4">
              Everything you need to run your crews
            </h2>
            <p className="text-lg text-zinc-600">
              From clock-in to payroll, Hines Time gives growing service businesses every tool in one simple platform.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((f, i) => {
              const Icon = f.icon;
              return (
                <div
                  key={f.title}
                  className="group h-full flex flex-col bg-white rounded-2xl border border-zinc-200 p-7 hover:shadow-xl hover:-translate-y-1 transition-all duration-300"
                  data-testid={`feature-card-${i}`}
                >
                  <div className="flex items-start justify-between mb-5">
                    <div className="w-12 h-12 bg-zinc-900 rounded-xl flex items-center justify-center group-hover:bg-amber-600 transition-colors duration-300">
                      <Icon className="w-6 h-6 text-white" />
                    </div>
                    <span className={`text-[11px] font-bold uppercase tracking-wide px-2.5 py-1 rounded-full border ${f.accent}`}>
                      {f.badge}
                    </span>
                  </div>
                  <h3 className="text-xl font-bold text-zinc-900 mb-2">{f.title}</h3>
                  <p className="text-zinc-600 leading-relaxed flex-1">{f.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-20 sm:py-28 bg-white border-y border-zinc-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-14 items-center">
            <div>
              <p className="text-xs font-bold uppercase tracking-wider text-[#B45309] mb-3">Why teams switch</p>
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-zinc-900 mb-5">
                Stop losing money on time theft &amp; errors
              </h2>
              <p className="text-lg text-zinc-600 mb-8">
                Manual timesheets cost businesses an average of 7% of gross payroll in overpayments.
                Hines Time automates everything with GPS verification and real-time tracking.
              </p>
              <div className="space-y-4">
                {benefits.map((b) => (
                  <div key={b} className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-emerald-600 mt-0.5 flex-shrink-0" />
                    <span className="text-zinc-700">{b}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="relative">
              <img
                src={BENEFITS_PHOTO}
                alt="Crew members reviewing work on site"
                className="w-full h-[320px] sm:h-[420px] object-cover rounded-3xl shadow-xl"
              />
              <div className="absolute -bottom-6 right-4 sm:-right-4 bg-white rounded-2xl shadow-xl border border-zinc-100 p-5 w-[250px]" data-testid="benefits-payroll-card">
                <div className="flex items-center gap-2 mb-2">
                  <ShieldCheck className="w-4 h-4 text-emerald-600" />
                  <p className="text-xs font-bold uppercase tracking-wide text-zinc-500">Payroll locked</p>
                </div>
                <p className="text-sm text-zinc-700">Pay period exported to CSV in <span className="font-bold text-zinc-900">under 1 minute</span></p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 sm:py-28" data-testid="testimonials-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl mb-14">
            <p className="text-xs font-bold uppercase tracking-wider text-[#B45309] mb-3">Trusted by crews like yours</p>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-zinc-900 mb-4">
              What service businesses say
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              {
                quote: 'Payroll used to take me a full Sunday. Now I lock the pay period, export, and I\u2019m done in ten minutes. The GPS clock-ins ended the "I was there at 7" debates for good.',
                name: 'Corey H.',
                role: 'Owner, Facilities Services',
                location: 'Ontario, Canada'
              },
              {
                quote: 'The before-and-after photo proof is what wins us contracts. Clients open their portal link, see the finished work with timestamps, and sign off without a single phone call.',
                name: 'Maria S.',
                role: 'Operations Manager, Commercial Cleaning',
                location: 'Texas, USA'
              },
              {
                quote: 'Half my crew speaks Spanish, the other half French. Everyone uses the app in their own language and clock-ins just work — even the QR kiosk at the yard gate.',
                name: 'Daniel R.',
                role: 'Site Supervisor, Landscaping & Grounds',
                location: 'Montreal, Canada'
              }
            ].map((t, i) => (
              <figure
                key={t.name}
                className="bg-white rounded-2xl border border-zinc-200 p-7 flex flex-col hover:shadow-xl hover:-translate-y-1 transition-all duration-300"
                data-testid={`testimonial-card-${i}`}
              >
                <div className="flex gap-1 mb-4" aria-label="5 out of 5 stars">
                  {[0, 1, 2, 3, 4].map((s) => (
                    <Star key={s} className="w-4 h-4 text-amber-500 fill-amber-500" />
                  ))}
                </div>
                <blockquote className="text-zinc-700 leading-relaxed flex-1">"{t.quote}"</blockquote>
                <figcaption className="mt-6 pt-5 border-t border-zinc-100 flex items-center gap-3">
                  <div
                    className="w-12 h-12 rounded-full bg-gradient-to-br from-amber-500 to-orange-700 flex items-center justify-center flex-shrink-0 shadow-lg shadow-orange-900/20"
                    aria-hidden="true"
                  >
                    <span className="text-white font-bold text-xl tracking-wide">
                      {t.name.split(' ').map((w) => w[0]).join('').slice(0, 2)}
                    </span>
                  </div>
                  <div className="min-w-0">
                    <p className="font-bold text-zinc-900">{t.name}</p>
                    <p className="text-sm text-zinc-500">{t.role}</p>
                    <p className="text-xs text-zinc-400 mt-0.5">{t.location}</p>
                  </div>
                </figcaption>
              </figure>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section id="pricing" className="py-20 sm:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <p className="text-xs font-bold uppercase tracking-wider text-[#B45309] mb-3">Simple pricing</p>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-zinc-900 mb-4">
              Transparent pricing that scales with you
            </h2>
            <p className="text-lg text-zinc-600">
              No hidden fees. No long-term contracts. Pay only for active users.
            </p>
          </div>

          {/* Currency toggle */}
          <div className="flex justify-center mb-10">
            <div className="inline-flex bg-white border border-zinc-200 rounded-xl p-1 shadow-sm" data-testid="currency-toggle">
              {Object.entries(PRICING).map(([code, cfg]) => (
                <button
                  key={code}
                  onClick={() => setCurrency(code)}
                  className={`px-4 py-2 min-h-[44px] rounded-lg text-sm font-semibold transition-colors ${
                    currency === code ? 'bg-zinc-900 text-white' : 'text-zinc-600 hover:text-zinc-900'
                  }`}
                  data-testid={`currency-toggle-${code}`}
                >
                  {cfg.flag} {cfg.label}
                </button>
              ))}
            </div>
          </div>

          <div className="max-w-3xl mx-auto">
            <div className="bg-white rounded-3xl border border-zinc-200 shadow-xl overflow-hidden" data-testid="pricing-card">
              <div className="bg-zinc-900 text-white px-8 sm:px-12 py-10 text-center">
                <span className="inline-block bg-amber-500/15 border border-amber-500/30 text-amber-300 px-4 py-1.5 rounded-full text-xs font-bold uppercase tracking-wide mb-5">
                  30-Day Free Trial — Credit Card Required
                </span>
                <h3 className="text-2xl font-bold mb-4">Pay As You Grow</h3>
                <div className="flex items-baseline justify-center gap-2">
                  <span className="text-5xl sm:text-6xl font-extrabold" data-testid="pricing-monthly-amount">
                    {p.symbol}{p.monthly}
                  </span>
                  <span className="text-lg text-[#E5E7EB]">per user/month</span>
                </div>
                <p className="text-[#E5E7EB] mt-3 text-sm font-normal">Plus a one-time setup fee of <span className="text-white font-semibold">{p.symbol}{p.setup}</span></p>
                <p className="text-amber-300 text-sm font-semibold mt-1">Billing starts after your 30-day free trial</p>
              </div>
              <div className="px-8 sm:px-12 py-10">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-3 mb-8">
                  {planFeatures.map((item) => (
                    <div key={item} className="flex items-center gap-3">
                      <CheckCircle className="w-4 h-4 text-emerald-600 flex-shrink-0" />
                      <span className="text-zinc-700 text-sm">{item}</span>
                    </div>
                  ))}
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-8 text-sm">
                  {['Add or remove users anytime', 'Pay only for active users', 'Cancel anytime, no contracts'].map((v) => (
                    <div key={v} className="flex items-center gap-2 bg-[#FAF8F5] border border-zinc-200 rounded-xl px-3 py-2.5">
                      <Zap className="w-4 h-4 text-amber-600 flex-shrink-0" />
                      <span className="text-zinc-700 font-medium">{v}</span>
                    </div>
                  ))}
                </div>
                <Button
                  onClick={() => navigate('/register')}
                  size="lg"
                  className="w-full bg-amber-600 hover:bg-amber-700 text-white h-14 text-lg rounded-xl font-semibold shadow-lg shadow-amber-600/25"
                  data-testid="pricing-start-trial-button"
                >
                  Start Your Free Trial Now
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
                <p className="text-center text-xs text-zinc-500 mt-4">
                  Credit card required • Free for 30 days • Then auto-billed • Cancel anytime
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="pt-20 sm:pt-28 pb-16 sm:pb-20 bg-[#FAF8F5]" data-testid="faq-section">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <p className="text-xs font-bold uppercase tracking-wider text-[#B45309] mb-3">Questions & answers</p>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-zinc-900 mb-4">
              Frequently asked questions
            </h2>
            <p className="text-lg text-zinc-600">
              Everything you need to know about billing, your free trial, and getting set up.
            </p>
          </div>
          <div className="space-y-3">
            {faqs.map((faq, i) => (
              <div
                key={faq.q}
                className={`bg-white rounded-2xl border transition-colors ${openFaq === i ? 'border-amber-300 shadow-md' : 'border-zinc-200 shadow-sm'}`}
                data-testid={`faq-item-${i}`}
              >
                <button
                  onClick={() => setOpenFaq(openFaq === i ? -1 : i)}
                  className="w-full flex items-center justify-between gap-4 px-6 py-5 text-left"
                  aria-expanded={openFaq === i}
                  data-testid={`faq-question-${i}`}
                >
                  <span className="font-semibold text-zinc-900">{faq.q}</span>
                  <ChevronDown className={`w-5 h-5 text-amber-600 flex-shrink-0 transition-transform ${openFaq === i ? 'rotate-180' : ''}`} />
                </button>
                {openFaq === i && (
                  <div className="px-6 pb-5 text-zinc-600 leading-relaxed" data-testid={`faq-answer-${i}`}>
                    {faq.a}
                  </div>
                )}
              </div>
            ))}
          </div>
          <p className="text-center text-sm text-zinc-500 mt-8">
            Still have questions?{' '}
            <a href="/contact" className="text-amber-700 font-semibold hover:underline" data-testid="faq-contact-link">Contact us</a>
            {' '}— we reply fast.
          </p>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 sm:py-24 bg-zinc-900">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-white mb-5">
            Ready to transform your workforce management?
          </h2>
          <p className="text-lg text-zinc-400 mb-9">
            Save time and money with Hines Time — trusted across North America, the UK, and Europe.
          </p>
          <Button
            onClick={() => navigate('/register')}
            size="lg"
            className="bg-amber-600 hover:bg-amber-700 text-white text-lg h-14 px-10 rounded-xl font-semibold shadow-lg shadow-amber-600/30 transition-transform hover:-translate-y-0.5"
            data-testid="cta-start-trial-button"
          >
            Get Started Free Today
            <ArrowRight className="w-5 h-5 ml-2" />
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-zinc-950 text-zinc-400 py-14 border-t border-zinc-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-10">
            <div>
              <div className="flex items-center gap-2 mb-4">
                <div className="w-8 h-8 bg-zinc-800 rounded-lg flex items-center justify-center">
                  <Clock className="w-4 h-4 text-amber-400" />
                </div>
                <span className="text-lg font-bold text-white">Hines Time</span>
              </div>
              <p className="text-sm leading-relaxed font-normal text-[#9CA3AF]">
                Modern timekeeping and job management platform built by Scalevisory for service companies and growing teams.
              </p>
              <div className="flex items-center gap-3 mt-5">
                <a
                  href="#"
                  aria-label="Hines Time on LinkedIn"
                  className="w-9 h-9 rounded-lg bg-zinc-800 flex items-center justify-center text-[#9CA3AF] hover:text-white hover:bg-zinc-700 transition-colors"
                  data-testid="footer-linkedin"
                >
                  <svg viewBox="0 0 24 24" fill="currentColor" className="w-4 h-4" aria-hidden="true">
                    <path d="M20.45 20.45h-3.55v-5.57c0-1.33-.02-3.04-1.85-3.04-1.85 0-2.14 1.45-2.14 2.94v5.67H9.36V9h3.41v1.56h.05c.47-.9 1.63-1.85 3.36-1.85 3.6 0 4.27 2.37 4.27 5.46v6.28zM5.34 7.43a2.06 2.06 0 1 1 0-4.12 2.06 2.06 0 0 1 0 4.12zM7.12 20.45H3.56V9h3.56v11.45z" />
                  </svg>
                </a>
                <a
                  href="#"
                  aria-label="Hines Time on X"
                  className="w-9 h-9 rounded-lg bg-zinc-800 flex items-center justify-center text-[#9CA3AF] hover:text-white hover:bg-zinc-700 transition-colors"
                  data-testid="footer-x-twitter"
                >
                  <svg viewBox="0 0 24 24" fill="currentColor" className="w-4 h-4" aria-hidden="true">
                    <path d="M18.9 2.5h3.2l-7 8.02L23.4 21.5h-6.46l-5.06-6.62-5.79 6.62H2.87l7.49-8.57L2.4 2.5h6.62l4.57 6.05 5.31-6.05zm-1.13 17.1h1.78L8.06 4.31H6.15L17.77 19.6z" />
                  </svg>
                </a>
              </div>
            </div>
            <div>
              <h4 className="text-white font-semibold mb-4 text-sm uppercase tracking-wide">Product</h4>
              <ul className="space-y-2.5 text-sm">
                <li><a href="/#features" className="hover:text-white transition-colors inline-block py-1" data-testid="footer-features">Features</a></li>
                <li><a href="/#pricing" className="hover:text-white transition-colors inline-block py-1" data-testid="footer-pricing">Pricing</a></li>
                <li><a href="/demo" className="hover:text-white transition-colors inline-block py-1" data-testid="footer-demo">Demo</a></li>
                <li><a href="/user-manual" className="hover:text-white transition-colors inline-block py-1" data-testid="footer-user-manual">User Manual</a></li>
              </ul>
            </div>
            <div>
              <h4 className="text-white font-semibold mb-4 text-sm uppercase tracking-wide">Company</h4>
              <ul className="space-y-2.5 text-sm">
                <li><a href="/about" className="hover:text-white transition-colors inline-block py-1" data-testid="footer-about">About Us</a></li>
                <li><a href="/contact" className="hover:text-white transition-colors inline-block py-1" data-testid="footer-contact">Contact</a></li>
                <li><a href="/support" className="hover:text-white transition-colors inline-block py-1" data-testid="footer-support">Support</a></li>
              </ul>
            </div>
            <div>
              <h4 className="text-white font-semibold mb-4 text-sm uppercase tracking-wide">Legal</h4>
              <ul className="space-y-2.5 text-sm">
                <li><a href="/terms" className="hover:text-white transition-colors inline-block py-1" data-testid="footer-terms">Terms of Service</a></li>
                <li><a href="/privacy" className="hover:text-white transition-colors inline-block py-1" data-testid="footer-privacy">Privacy Policy</a></li>
                <li><a href="/security" className="hover:text-white transition-colors inline-block py-1" data-testid="footer-security">Security</a></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-zinc-800 mt-10 pt-8 text-center text-sm">
            <p>&copy; 2026 Hines Time by Scalevisory. All rights reserved.</p>
            <p className="mt-2">Available in North America, UK, and Europe</p>
            <p className="mt-2 text-xs text-[#9CA3AF]">Part of the Scalevisory family of business solutions</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Landing;
